import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Message,
  Space,
  Card,
  Descriptions,
  Tag,
  Checkbox,
  Alert,
  Typography,
  Divider
} from '@arco-design/web-react';
import {
  IconUser,
  IconBook,
  IconFile,
  IconCheckCircle
} from '@arco-design/web-react/icon';
import { useSubmitOnboarding } from '../hooks/useOnboardingData';
import { formatDate } from '../../common/helpers/dateHelpers';
import './ReviewStep.css';
import ReportProblemButton from '../components/ReportProblemButton';

const { Paragraph } = Typography;

function ReviewStep({ onBack, data, requestedChanges, previewMode = false, freeMode = false, roleVariant = 'teacher' }) {
  const { t } = useTranslation('onboarding');
  const submitMutation = useSubmitOnboarding();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const pendingChanges = Array.isArray(requestedChanges)
    ? requestedChanges.filter((change) => !change.resolved)
    : [];
  const isAdmin = roleVariant === 'admin';
  const isCorporate = roleVariant === 'corporate';

  const handleSubmit = async () => {
    if (previewMode) {
      Message.success(t('steps.review.messages.simulationComplete'));
      return;
    }
    if (!acceptedTerms) {
      Message.warning(t('steps.review.messages.mustAcceptTerms'));
      return;
    }
    if (hasMissingDocuments) {
      Message.warning(t('steps.review.messages.missingDocuments'));
      return;
    }
    if (pendingChanges.length > 0) {
      Message.warning(t('steps.review.messages.pendingChanges'));
      return;
    }

    submitMutation.mutate(undefined, {
      onSuccess: () => {
        Message.success({
          content: t('steps.review.messages.submitSuccess'),
          duration: 2000
        });
        setTimeout(() => {
          window.location.href = '/onboarding/status';
        }, 1500);
      },
      onError: () => {
        Message.error(t('steps.review.messages.submitError'));
      }
    });
  };

  const { identity, teacherDetails, documents } = data;
  const documentEntries = documents
    ? Object.entries(documents).filter(([key]) => key !== 'notes')
    : [];
  const notesText = documents?.notes || '';
  const governmentIdLabel = identity?.government_id_type === 'OTHER'
    ? (identity?.government_id_type_other || 'Otro')
    : identity?.government_id_type;

  // Helper para capitalizar
  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Helper para obtener label de documento
  const getDocumentLabel = (key) => {
    if (key === 'curp') return t('steps.review.documentLabels.curp');
    if (key === 'photo') return t('steps.review.documentLabels.photo');
    if (key === 'cv') return t('steps.review.documentLabels.cv');
    if (key === 'professional_license') return t('steps.review.documentLabels.professionalLicense');
    if (key === 'sni_certificate') return t('steps.review.documentLabels.sniCertificate');
    if (key === 'representative_id') return t('steps.review.documentLabels.representativeId');
    if (key === 'power_of_attorney') return t('steps.review.documentLabels.powerOfAttorney');
    if (key === 'fiscal_certificate') return t('steps.review.documentLabels.fiscalCertificate');
    if (key.startsWith('degree_')) {
      const index = parseInt(key.split('_')[1]);
      const degree = teacherDetails?.degrees?.[index];
      if (degree) {
        return t('steps.review.documentLabels.degreeTitle', { level: capitalize(degree.level) });
      }
      return `Título ${index + 1}`;
    }
    return key;
  };

  const requiredDocuments = [];
  if (isCorporate) {
    requiredDocuments.push('representative_id', 'power_of_attorney', 'fiscal_certificate');
  } else {
    requiredDocuments.push('curp', 'photo', 'cv');
    const degreeCount = Array.isArray(teacherDetails?.degrees) ? teacherDetails.degrees.length : 0;
    for (let i = 0; i < degreeCount; i += 1) {
      requiredDocuments.push(`degree_${i}`);
    }
    if (!isAdmin && teacherDetails?.is_sni_member) {
      requiredDocuments.push('sni_certificate');
    }
  }

  const missingDocuments = requiredDocuments.filter((key) => !documents?.[key]);
  const missingDocumentLabels = missingDocuments.map(getDocumentLabel);
  const hasMissingDocuments = missingDocuments.length > 0;
  const reviewStepLabel = t('steps.review.title', { step: (isAdmin || isCorporate) ? 4 : 5 });

  return (
    <div className="review-step">
      <div className="step-header">
        <h2>{reviewStepLabel}</h2>
        <p>{t('steps.review.description')}</p>
      </div>

      <Alert
        type="info"
        content={t('steps.review.infoAlert')}
        closable
        style={{ marginBottom: 24 }}
      />
      {pendingChanges.length > 0 && (
        <Alert
          type="warning"
          content={
            <div>
              <strong>{t('steps.review.pendingChangesTitle')}</strong>
              <ul style={{ margin: '8px 0 0 18px' }}>
                {pendingChanges.map((change, index) => (
                  <li key={`${change.field}-${index}`}>
                    {change.label}: {change.message}
                  </li>
                ))}
              </ul>
            </div>
          }
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Datos Personales */}
      <Card
        className="review-section"
        title={
          <span>
            <IconUser style={{ marginRight: 8 }} />
            {isCorporate ? t('steps.review.labels.representativeData') : t('steps.review.labels.personalData')}
          </span>
        }
        extra={
          <Button
            type="text"
            size="small"
            onClick={() => onBack(1)}
          >
            {t('steps.review.labels.edit')}
          </Button>
        }
      >
        <Descriptions
          column={2}
          data={[
            {
              label: t('steps.review.labels.fullName'),
              value: `${identity?.first_name || ''} ${identity?.middle_name || ''} ${identity?.last_name || ''} ${identity?.second_last_name || ''}`.trim()
            },
            {
              label: t('steps.review.labels.dateOfBirth'),
              value: formatDate(identity?.date_of_birth)
            },
            ...(isCorporate
              ? [
                {
                  label: t('steps.review.labels.contactEmail'),
                  value: identity?.contact_email || 'N/A'
                }
              ]
              : [
                {
                  label: t('steps.review.labels.gender'),
                  value: identity?.gender === 'M' ? t('steps.review.genderOptions.M') : identity?.gender === 'F' ? t('steps.review.genderOptions.F') : t('steps.review.genderOptions.O')
                }
              ]),
            {
              label: t('steps.review.labels.identification'),
              value: `${governmentIdLabel || 'N/A'}: ${identity?.government_id || 'N/A'}`
            },
            {
              label: t('steps.review.labels.phone'),
              value: `${identity?.phone_country_code || '+52'} ${identity?.phone}`
            },
            ...(isCorporate ? [] : [
              {
                label: t('steps.review.labels.emergencyContact'),
                value: identity?.emergency_contact_name ? (
                  <>
                    {identity.emergency_contact_name} ({identity.emergency_contact_relationship || 'N/A'})<br />
                    {identity.emergency_phone_country_code || '+52'} {identity.emergency_phone || t('steps.review.labels.noPhone')}
                  </>
                ) : t('steps.review.labels.notProvided')
              }
            ]),
            ...(isCorporate ? [] : [
              {
                label: t('steps.review.labels.addressLabel'),
                value: (
                  <Paragraph style={{ margin: 0 }}>
                    {identity?.address}<br />
                    {identity?.country}<br />
                    {identity?.state}, {identity?.city}<br />
                    {identity?.postal_code}
                  </Paragraph>
                )
              }
            ])
          ]}
        />
      </Card>

      {isCorporate ? (
        <>
          <Card
            className="review-section"
            title={
              <span>
                <IconBook style={{ marginRight: 8 }} />
                {t('steps.review.labels.corporateData')}
              </span>
            }
            extra={(
              <Button type="text" size="small" onClick={() => onBack(2)}>
                {t('steps.review.labels.edit')}
              </Button>
            )}
          >
            <Descriptions
              column={2}
              data={[
                { label: t('steps.review.labels.legalName'), value: teacherDetails?.company_legal_name || 'N/A' },
                { label: t('steps.review.labels.tradeName'), value: teacherDetails?.trade_name || 'N/A' },
                { label: t('steps.review.labels.rfc'), value: teacherDetails?.rfc || 'N/A' },
                { label: t('steps.review.labels.industry'), value: teacherDetails?.industry || 'N/A' },
                { label: t('steps.review.labels.website'), value: teacherDetails?.website || 'N/A' }
              ]}
            />
            <Divider orientation="left">{t('steps.review.labels.commercialAddress')}</Divider>
            <Paragraph style={{ margin: 0 }}>
              {teacherDetails?.commercial_address || 'N/A'}<br />
              {teacherDetails?.commercial_country || 'N/A'}<br />
              {teacherDetails?.commercial_state || 'N/A'}, {teacherDetails?.commercial_city || 'N/A'}<br />
              {teacherDetails?.commercial_postal_code || 'N/A'}
            </Paragraph>
            <Divider orientation="left">{t('steps.review.labels.fiscalAddress')}</Divider>
            <Paragraph style={{ margin: 0 }}>
              {teacherDetails?.fiscal_address || 'N/A'}<br />
              {teacherDetails?.fiscal_country || 'N/A'}<br />
              {teacherDetails?.fiscal_state || 'N/A'}, {teacherDetails?.fiscal_city || 'N/A'}<br />
              {teacherDetails?.fiscal_postal_code || 'N/A'}
            </Paragraph>
          </Card>

          <Card
            className="review-section"
            title={
              <span>
                <IconUser style={{ marginRight: 8 }} />
                {t('steps.review.labels.operatorData')}
              </span>
            }
            extra={(
              <Button type="text" size="small" onClick={() => onBack(3)}>
                {t('steps.review.labels.edit')}
              </Button>
            )}
          >
            {Array.isArray(teacherDetails?.operator_contacts) && teacherDetails.operator_contacts.length > 0 ? (
              teacherDetails.operator_contacts.map((contact, index) => (
                <Card
                  key={index}
                  style={{ marginBottom: 16, background: '#f7f8fa' }}
                  bordered={false}
                >
                  <Descriptions
                    column={1}
                    data={[
                      { label: t('steps.review.labels.operatorName'), value: contact.full_name || 'N/A' },
                      { label: t('steps.review.labels.operatorEmail'), value: contact.email || 'N/A' },
                      { label: t('steps.review.labels.operatorPhone'), value: contact.phone || 'N/A' }
                    ]}
                  />
                </Card>
              ))
            ) : (
              <Paragraph style={{ margin: 0 }}>{t('steps.review.labels.noOperators')}</Paragraph>
            )}
          </Card>
        </>
      ) : (
      /* Información Académica */
      <Card
        className="review-section"
        title={
          <span>
            <IconBook style={{ marginRight: 8 }} />
            {isAdmin ? t('steps.review.labels.academicLabor') : t('steps.review.labels.academicProfessional')}
          </span>
        }
        extra={
          <Button
            type="text"
            size="small"
            onClick={() => onBack(2)}
          >
            {t('steps.review.labels.edit')}
          </Button>
        }
      >
        <Descriptions
          column={2}
          data={[
            {
              label: t('steps.review.labels.maxDegree'),
              value: <Tag color="blue">{capitalize(teacherDetails?.highest_degree)}</Tag>
            },
            {
              label: t('steps.review.labels.degreeCount'),
              value: teacherDetails?.degree_count || teacherDetails?.degrees?.length || 0
            },
            {
              label: isAdmin ? t('steps.review.labels.workExperience') : t('steps.review.labels.teachingExperience'),
              value: `${isAdmin ? (teacherDetails?.years_work_experience || 0) : (teacherDetails?.years_teaching_experience || 0)} ${t('steps.review.labels.years', { count: isAdmin ? (teacherDetails?.years_work_experience || 0) : (teacherDetails?.years_teaching_experience || 0) })}`
            }
          ]}
        />

        {!isAdmin && (
          <Descriptions
            column={2}
            data={[
              {
                label: t('steps.review.labels.hoursPerWeek'),
                value: `${teacherDetails?.max_hours_per_week || 0} ${t('steps.review.labels.hours', { count: teacherDetails?.max_hours_per_week || 0 })}`
              },
              {
                label: t('steps.review.labels.sniMember'),
                value: teacherDetails?.is_sni_member ? (
                  <Tag color="green">{t('steps.review.labels.sniYes', { level: teacherDetails.sni_level || 'N/A' })}</Tag>
                ) : (
                  <Tag color="gray">{t('steps.review.labels.sniNo')}</Tag>
                )
              }
            ]}
          />
        )}
        {/* Títulos Académicos */}
        {teacherDetails?.degrees && teacherDetails.degrees.length > 0 && (
          <>
            <Divider orientation="left">{t('steps.review.labels.degreesTitle')}</Divider>
            {teacherDetails.degrees.map((degree, index) => (
              <Card
                key={index}
                style={{ marginBottom: 16, background: '#f7f8fa' }}
                bordered={false}
              >
                <Descriptions
                  column={2}
                  data={[
                    {
                      label: t('steps.review.labels.degreeLevel'),
                      value: <Tag color="blue">{capitalize(degree.level)}</Tag>
                    },
                    {
                      label: t('steps.review.labels.degreeInstitution'),
                      value: degree.institution
                    },
                    {
                      label: t('steps.review.labels.degreeField'),
                      value: degree.field
                    },
                    {
                      label: t('steps.review.labels.degreeGradYear'),
                      value: degree.graduation_status === 'cursando'
                        ? t('shared.degrees.cursando')
                        : (degree.graduation_year || 'N/A')
                    }
                  ]}
                />
              </Card>
            ))}
          </>
        )}

        {/* Instituciones Previas */}
        {teacherDetails?.previous_institutions && teacherDetails.previous_institutions.length > 0 && (
          <>
            <Divider orientation="left">{t('steps.review.labels.previousExperience')}</Divider>
            {teacherDetails.previous_institutions.map((inst, index) => (
              <Card
                key={index}
                style={{ marginBottom: 16, background: '#f7f8fa' }}
                bordered={false}
              >
                <Descriptions
                  column={1}
                  data={[
                    {
                      label: t('steps.review.labels.institutionLabel'),
                      value: <Tag color="arcoblue">{inst.name}</Tag>
                    },
                    {
                      label: t('steps.review.labels.yearsExperience'),
                      value: `${inst.years} ${t('steps.review.labels.years', { count: inst.years })}`
                    },
                    {
                      label: isAdmin ? t('steps.review.labels.position') : t('steps.review.labels.subjectsTaught'),
                      value: isAdmin ? inst.position : (
                        <div>
                          {Array.isArray(inst.taught_subjects) ? (
                            inst.taught_subjects.map((subject, subIndex) => (
                              <Tag key={subIndex} color="green" style={{ marginBottom: 4, marginRight: 4 }}>
                                {subject}
                              </Tag>
                            ))
                          ) : (
                            <span>{inst.taught_subjects}</span>
                          )}
                        </div>
                      )
                    },
                    ...(isAdmin ? [
                      {
                        label: t('steps.review.labels.responsibilities'),
                        value: inst.responsibilities || 'N/A'
                      },
                      {
                        label: t('steps.review.labels.achievements'),
                        value: inst.achievements || 'N/A'
                      }
                    ] : [])
                  ]}
                />
              </Card>
            ))}
          </>
        )}

        {/* Disponibilidad */}
        {!isAdmin && teacherDetails?.availability_schedule && teacherDetails.availability_schedule.length > 0 && (
          <>
            <Divider orientation="left">{t('steps.review.labels.scheduleAvailability')}</Divider>
            <div>
              {teacherDetails.availability_schedule.map((slot, index) => (
                <Tag key={index} color="green" style={{ marginBottom: 8, marginRight: 8 }}>
                  {slot.day}: {slot.start_time} - {slot.end_time}
                  {slot.modality ? ` · ${capitalize(slot.modality)}` : ''}
                  {(slot.modality || '').toLowerCase() === 'presencial' && slot.city ? ` · ${slot.city}` : ''}
                </Tag>
              ))}
            </div>
          </>
        )}

        {/* URLs */}
        {(teacherDetails?.linkedin_url || teacherDetails?.portfolio_url) && (
          <Divider orientation="left">{t('steps.review.labels.linksTitle')}</Divider>
        )}
        {teacherDetails?.linkedin_url && (
          <Descriptions
            column={1}
            data={[
              {
                label: t('steps.review.labels.linkedin'),
                value: (
                  <a href={teacherDetails.linkedin_url} target="_blank" rel="noopener noreferrer">
                    {teacherDetails.linkedin_url}
                  </a>
                )
              }
            ]}
          />
        )}
        {teacherDetails?.portfolio_url && (
          <Descriptions
            column={1}
            data={[
              {
                label: t('steps.review.labels.portfolio'),
                value: (
                  <a href={teacherDetails.portfolio_url} target="_blank" rel="noopener noreferrer">
                    {teacherDetails.portfolio_url}
                  </a>
                )
              }
            ]}
          />
        )}
      </Card>
      )}

      {/* Documentos */}
      <Card
        className="review-section"
        title={
          <span>
            <IconFile style={{ marginRight: 8 }} />
            {t('steps.review.labels.documentsTitle', { count: documentEntries.length })}
          </span>
        }
      >
        {hasMissingDocuments && (
          <Alert
            type="warning"
            content={
              <div>
                <strong>{t('steps.review.labels.missingDocsTitle')}</strong>
                <ul className="documents-missing-list">
                  {missingDocumentLabels.map((label, index) => (
                    <li key={`${label}-${index}`}>{label}</li>
                  ))}
                </ul>
                <div className="documents-missing-hint">
                  {isCorporate
                    ? t('steps.review.labels.missingDocsHintCorporate')
                    : (isAdmin
                      ? t('steps.review.labels.missingDocsHintAdmin')
                      : t('steps.review.labels.missingDocsHintTeacher'))}
                </div>
              </div>
            }
            style={{ marginBottom: 16 }}
          />
        )}
        <div className="documents-list">
          {documentEntries.map(([key, doc]) => (
            <div key={key} className="document-item">
              <IconCheckCircle style={{ color: 'var(--color-success-6)', marginRight: 8 }} />
              <span>
                {getDocumentLabel(key)}: {doc.file_name} ({(doc.file_size / 1024).toFixed(1)} KB)
              </span>
            </div>
          ))}

          {documentEntries.length === 0 && (
            <p className="documents-empty">{t('steps.review.labels.noDocuments')}</p>
          )}

          {notesText && (
            <div className="documents-notes-review">
              <Divider orientation="left">{t('steps.review.labels.notesTitle')}</Divider>
              <Paragraph style={{ margin: 0 }}>{notesText}</Paragraph>
            </div>
          )}
        </div>
      </Card>

      {/* Términos y Condiciones */}
      <div className="terms-section">
        <Checkbox
          checked={acceptedTerms}
          onChange={setAcceptedTerms}
        >
          {t('steps.review.terms.accept')}{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); }}>
            {t('steps.review.terms.termsAndConditions')}
          </a>
          {' '}{t('steps.review.terms.and')}{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); }}>
            {t('steps.review.terms.privacyPolicy')}
          </a>
          {' '}{t('steps.review.terms.ofSmartHub')}
        </Checkbox>
      </div>

      {/* Botones */}
      <div className="step-actions">
        <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
          <Button
            onClick={() => onBack((isAdmin || isCorporate) ? 3 : 2)}
            size="large"
          >
            {t('shared.buttons.back')}
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitMutation.isPending}
            disabled={!acceptedTerms || hasMissingDocuments}
            size="large"
            icon={<IconCheckCircle />}
          >
            {t('steps.review.buttons.submitApplication')}
          </Button>
          {previewMode && freeMode && (
            <Button size="large" onClick={handleSubmit}>
              {t('steps.review.buttons.finishSimulation')}
            </Button>
          )}
          <ReportProblemButton stepLabel={reviewStepLabel} previewMode={previewMode} asButton />
        </Space>

        {!acceptedTerms && (
          <div className="validation-message">
            <small>⚠️ {t('steps.review.terms.mustAccept')}</small>
          </div>
        )}
        {hasMissingDocuments && (
          <div className="validation-message">
            <small>⚠️ {t('steps.review.terms.missingDocs')}</small>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviewStep;
